import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    NgZone,
    OnInit,
    ViewEncapsulation,
    input,
    output,
    signal,
    computed,
    linkedSignal,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ItemService } from "../../util/items/item.service";
import {
    DataIngestionService,
    IngestionPoint,
    ReportingFieldInfo,
    SearchRequest,
    ReportRequest,
    AggregationSpec,
    SearchResponse,
    ReportResponse,
    IngestionSavedQuery,
    SavedQueryWithData,
    IngestionSubmission,
    AnswerType,
    IngestionFieldAnswer,
} from "./data.ingestion.service";
import { PredicateNode, PredicateType } from "./ingestion.predicate.builder.component";
import { ContactService } from "../../services/contact.service";
import { Table } from "../s25-table/Table";
import { UserprefService } from "../../services/userpref.service";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { S25ItemI } from "../../pojo/S25ItemI";
import { Item } from "../../pojo/Item";
import { S25Util } from "../../util/s25-util";
import { NameService } from "../../services/name.service";
import { AlertService } from "../../services/alert.service";
import {
    NameResolvableFieldType,
    NAME_RESOLVABLE_FIELD_TYPES,
    isNameResolvableFieldType,
} from "./data.ingestion.const";

@TypeManagerDecorator("s25-ng-ingestion-search-report")
@Component({
    selector: "s25-ng-ingestion-search-report",
    template: `
        <div class="ingestion-search-report">
            <div class="mode-selection">
                <h3>{{ pageHeading() }}</h3>

                <div class="saved-queries">
                    <label>{{ savedItemLabel() }}:</label>
                    <select
                        class="c-input"
                        (change)="loadSavedQuery($event)"
                        [value]="currentSavedQuery()?.queryId || ''"
                    >
                        <option value="">{{ savedItemPlaceholder() }}</option>
                        @for (query of getFilteredSavedQueries(); track query.queryId) {
                            <option [value]="query.queryId">{{ query.queryName }}</option>
                        }
                    </select>
                    @if (currentSavedQuery()) {
                        <button class="aw-button aw-button--danger--outline" (click)="deleteSavedQuery()">
                            Delete
                        </button>
                    }
                    <span class="saved-items-note"
                        >Saved searches (Search) and saved aggregations (Report) use separate lists.</span
                    >
                </div>
            </div>

            <div class="form-selection">
                <h4>Select Forms <span class="required-asterisk">*</span></h4>
                <p class="form-selection-help">
                    You must either select specific forms below or check "Search across ALL forms." Selecting specific
                    forms is recommended for cleaner results and better performance.
                </p>
                <s25-ng-ingestion-search-form-selector
                    [selectedForms]="selectedForms()"
                    [searchAllForms]="searchAllForms()"
                    (formsChanged)="onFormsChanged($event)"
                    (searchAllChanged)="onSearchAllChanged($event)"
                >
                </s25-ng-ingestion-search-form-selector>
            </div>

            <div class="filter-selection">
                <h4>Filter by Organization</h4>
                @if (showMultiselects()) {
                    <s25-ng-multiselect-search-criteria
                        [type]="'organizations'"
                        [selectedItems]="selectedOrganizations()"
                        (changed)="onOrganizationsChanged($event)"
                        [showResults]="true"
                    >
                    </s25-ng-multiselect-search-criteria>
                }
            </div>

            <div class="filter-selection">
                <h4>Filter by Event</h4>
                @if (showMultiselects()) {
                    <s25-ng-multiselect-search-criteria
                        [type]="'events'"
                        [selectedItems]="selectedEvents()"
                        (changed)="onEventsChanged($event)"
                        [showResults]="true"
                    >
                    </s25-ng-multiselect-search-criteria>
                }
            </div>

            @if (availableFields().length > 0) {
                <div class="field-selection">
                    <h4>
                        {{ mode() === "search" ? "Select Fields to Display" : "Select Fields to Group By" }}
                        &nbsp;<span class="required-asterisk">*</span>
                    </h4>
                    <s25-ng-ingestion-field-selector
                        [availableFields]="availableFields()"
                        [selectedFields]="getSelectedFieldsForSelector()"
                        [mode]="mode()"
                        (fieldsChanged)="onFieldSelectionChanged($event)"
                    >
                    </s25-ng-ingestion-field-selector>
                </div>
            }

            @if (mode() === "aggregations") {
                <div class="aggregation-selection">
                    <h4>Select Aggregations <span class="required-asterisk">*</span></h4>
                    <s25-ng-aggregation-selector
                        [availableFields]="availableFields()"
                        [selectedAggregations]="selectedAggregations()"
                        (aggregationsChanged)="onAggregationsChanged($event)"
                    >
                    </s25-ng-aggregation-selector>
                </div>
            }

            <div class="predicate-builder">
                <h4>Build Search Criteria</h4>
                <s25-ng-predicate-builder
                    [availableFields]="availableFields()"
                    [predicate]="predicate()"
                    (predicateChanged)="onPredicateChanged($event)"
                >
                </s25-ng-predicate-builder>
            </div>

            <div class="save-name-input">
                <label>{{ savedItemNameLabel() }}:</label>
                <input
                    class="c-input"
                    type="text"
                    [ngModel]="saveName()"
                    (ngModelChange)="saveName.set($event)"
                    [placeholder]="savedItemNamePlaceholder()"
                    [disabled]="!canSave()"
                />
                <span class="help-text"
                    >At least one field (Search) or one complete aggregation (Aggregations) is required to save.</span
                >
            </div>

            <s25-ng-fading-success-msg [(message)]="successMessage"></s25-ng-fading-success-msg>
            <div class="actions">
                <button class="aw-button aw-button--primary" (click)="execute()" [disabled]="!canExecute()">
                    {{ mode() === "search" ? "Search" : "Run aggregations" }}
                </button>
                <button
                    class="aw-button aw-button--outline"
                    (click)="save()"
                    [disabled]="!canSave() || !saveName()?.trim()"
                >
                    Save
                </button>
                <button
                    class="aw-button aw-button--outline"
                    (click)="saveAs()"
                    [disabled]="!canSave() || !saveName()?.trim()"
                >
                    Save As
                </button>
                <button class="aw-button aw-button--outline" (click)="resetCriteria()">Reset</button>
            </div>

            @if (init()) {
                @if (mode() === "search" && searchTableConfig()) {
                    <div class="results">
                        <h4>Results</h4>
                        <s25-ng-table
                            [dataSource]="searchTableConfig()!"
                            [columnSortable]="false"
                            [unlimitedWidth]="true"
                            [hasTotalRowCount]="true"
                            [hasRefresh]="true"
                            [hasExport]="true"
                            [exportFilename]="exportFilename()"
                            [exportSheetName]="exportSheetName()"
                        >
                        </s25-ng-table>
                    </div>
                }
                @if (mode() === "aggregations" && reportTableConfig()) {
                    <div class="results">
                        <h4>Results</h4>
                        <s25-ng-table
                            [dataSource]="reportTableConfig()!"
                            [columnSortable]="true"
                            [unlimitedWidth]="true"
                            [hasTotalRowCount]="true"
                            [hasRefresh]="true"
                            [hasExport]="true"
                            [exportFilename]="exportFilename()"
                            [exportSheetName]="exportSheetName()"
                        >
                        </s25-ng-table>
                    </div>
                }
            }
        </div>
    `,
    styles: `
        .ingestion-search-report {
            padding: 20px;
            max-width: 1200px;
            margin: 0 auto;
        }

        .mode-selection {
            margin-bottom: 30px;
        }

        .mode-toggle {
            margin-top: 15px;
        }

        .toggle-container {
            display: inline-flex;
            background: var(--color--surface-2-bg);
            border-radius: 8px;
            padding: 4px;
            border: 1px solid var(--color--border);
        }

        .toggle-option {
            padding: 8px 16px;
            border: none;
            background: transparent;
            cursor: pointer;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            color: var(--color--text-muted);
            transition: all 0.2s ease;
            min-width: 80px;
        }

        .toggle-option.active {
            background: var(--color--surface-bg);
            color: var(--color--link-text);
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .toggle-option:hover:not(.active) {
            color: var(--color--text-strong);
        }

        .saved-queries {
            margin-top: 15px;
            padding: 10px;
            background-color: var(--color--surface-2-bg);
            border-radius: 4px;

            label {
                display: block;
                margin-bottom: 5px;
                font-weight: bold;
            }

            select {
                width: 100%;
                max-width: 300px;
                margin-right: 10px;
            }

            button {
                margin-left: 10px;
            }

            .saved-items-note {
                display: block;
                margin-top: 8px;
                font-size: 0.875em;
                color: var(--color--text-muted);
            }
        }

        .save-name-input {
            margin-top: 15px;
            margin-bottom: 20px;
            padding: 10px;
            background-color: var(--color--surface-2-bg);
            border-radius: 4px;

            label {
                display: block;
                margin-bottom: 5px;
                font-weight: bold;
            }

            input {
                width: 100%;
                max-width: 300px;
                padding: 8px 12px;
                border: 1px solid var(--color--border-bold);
                border-radius: 4px;
                font-size: 14px;
            }

            input:disabled {
                background-color: var(--color--surface-2-bg);
                color: var(--color--text-muted);
            }

            .help-text {
                display: block;
                margin-top: 5px;
                font-size: 0.875em;
                color: var(--color--text-muted);
            }
        }

        .form-selection,
        .field-selection,
        .aggregation-selection,
        .predicate-builder,
        .filter-selection {
            margin-bottom: 30px;
            padding: 20px;
            border: 1px solid var(--color--border);
            border-radius: 4px;
        }

        .form-selection .form-selection-help {
            margin: 0 0 15px 0;
            font-size: 0.875em;
            color: var(--color--text-muted);
        }

        .actions {
            margin-bottom: 30px;
            display: flex;
            gap: 10px;
        }

        .btn {
            padding: 10px 20px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
        }

        .btn-primary {
            background: var(--color--link-text);
            color: white;
        }

        .btn-secondary {
            background: var(--color--text-muted);
            color: white;
        }

        .btn-secondary:hover:not(:disabled) {
            background: var(--color--text);
        }

        .btn-secondary:disabled {
            background: var(--color--text-muted);
            opacity: 0.6;
            cursor: not-allowed;
        }

        .btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }

        .results {
            margin-top: 30px;
        }

        .no-results {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
        }

        h3,
        h4 {
            margin-top: 0;
            color: var(--color--text-strong);
        }

        .required-asterisk {
            color: var(--color--danger);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class IngestionSearchReportComponent implements OnInit {
    /** Parent route or host fixes Search vs Aggregations; no in-page toggle. */
    initialMode = input<"search" | "aggregations">("search");
    resultsChanged = output<any>();

    private readonly itemService = inject(ItemService);

    readonly mode = linkedSignal(() => this.initialMode());

    pageHeading = computed(() => (this.mode() === "search" ? "Submission search" : "Submission aggregations"));
    selectedForms = signal<IngestionPoint[]>([]);
    searchAllForms = signal<boolean>(false);
    availableFields = signal<ReportingFieldInfo[]>([]);
    selectedFieldInfos = signal<ReportingFieldInfo[]>([]);
    selectedAggregations = signal<AggregationSpec[]>([]);
    predicate = signal<PredicateNode | null>(null);
    results = signal<SearchResponse | ReportResponse | null>(null);
    searchTableConfig = signal<Table.DataSource | null>(null);
    reportTableConfig = signal<Table.DataSource | null>(null);
    savedQueries = signal<IngestionSavedQuery[]>([]);
    currentSavedQuery = signal<SavedQueryWithData | null>(null);
    loading = signal<boolean>(false);
    currentPageKey = signal<string | null>(null);
    saveName = signal<string>("");
    init = signal(false);
    dateTimeFormat = signal<string>("");
    specialFieldNames = signal<Map<string, string>>(new Map());
    selectedOrganizationIds = signal<number[]>([]);
    selectedEventIds = signal<number[]>([]);
    showMultiselects = signal<boolean>(true);
    successMessage = signal<string>("");

    savedItemLabel = computed(() => (this.mode() === "search" ? "Saved searches" : "Saved aggregations"));
    savedItemPlaceholder = computed(() =>
        this.mode() === "search" ? "Select a saved search..." : "Select a saved aggregation...",
    );
    savedItemNameLabel = computed(() => (this.mode() === "search" ? "Search name" : "Aggregation name"));
    savedItemNamePlaceholder = computed(() =>
        this.mode() === "search" ? "Enter a name for this search..." : "Enter a name for this aggregation...",
    );
    exportFilename = computed(() => {
        const name = this.sanitizeFilename(this.currentSavedQuery()?.queryName || this.saveName()?.trim() || "");
        const base = name || (this.mode() === "search" ? "ingestion-search" : "ingestion-aggregations");
        return `${base}-results.xlsx`;
    });
    exportSheetName = computed(() => (this.mode() === "search" ? "Search results" : "Aggregation results"));

    private sanitizeFilename(name: string): string {
        if (!name?.trim()) return "";
        return name.replace(/[/\\:*?"<>|]/g, "-").trim();
    }

    constructor(
        private elem: ElementRef,
        private z: NgZone,
    ) {}

    async ngOnInit() {
        try {
            const format = await UserprefService.getS25DateTimeformat();
            this.dateTimeFormat.set(format);
        } catch (error) {
            S25Util.showError(error, "Error loading date/time format");
            // Set fallback format
            this.dateTimeFormat.set("MMM dd, yyyy h:mm a");
        } finally {
            this.init.set(true);
        }

        this.loadIngestionPoints();
        this.loadSavedQueries();
    }

    async loadIngestionPoints() {
        try {
            // Don't pre-select any forms - let user select manually
            this.selectedForms.set([]);
            // Load available fields after setting forms
            this.loadAvailableFields();
        } catch (error) {
            S25Util.showError(error, "Error loading ingestion points");
        }
    }

    async loadSavedQueries() {
        try {
            const queries = await DataIngestionService.getSavedQueries();
            this.savedQueries.set(queries);
        } catch (error) {
            S25Util.showError(error, "Error loading saved queries");
        }
    }

    getFilteredSavedQueries(): IngestionSavedQuery[] {
        const currentMode = this.mode();
        return this.savedQueries().filter((query) => {
            if (currentMode === "search") {
                return query.queryType === "SEARCH";
            }
            return query.queryType === "REPORT";
        });
    }

    /**
     * Reset component state when loading saved queries
     */
    private resetModeState() {
        this.selectedFieldInfos.set([]);
        this.selectedAggregations.set([]);
        this.selectedForms.set([]);
        this.searchAllForms.set(false);
        this.results.set(null);
        this.availableFields.set([]);
    }

    resetCriteria() {
        this.selectedFieldInfos.set([]);
        this.selectedAggregations.set([]);
        this.selectedForms.set([]);
        this.searchAllForms.set(false);
        this.availableFields.set([]);
        this.predicate.set({ id: "root_" + S25Util.generateQuickGUID(), op: "And" as const, children: [] });
        this.selectedOrganizationIds.set([]);
        this.selectedEventIds.set([]);
        this.selectedOrganizationsWithNames.set([]);
        this.selectedEventsWithNames.set([]);
        this.results.set(null);
        this.searchTableConfig.set(null);
        this.reportTableConfig.set(null);
        this.currentSavedQuery.set(null);
        this.saveName.set("");
        this.currentPageKey.set(null);
        this.successMessage.set("");
        this.recreateMultiselects();
    }

    /**
     * Load forms from ingestion point IDs
     */
    private async loadFormsFromIds(ingestionPointIds: number[] | undefined, searchAllForms: boolean) {
        if (searchAllForms) {
            this.searchAllForms.set(true);
            this.selectedForms.set([]);
        } else if (ingestionPointIds && ingestionPointIds.length > 0) {
            this.searchAllForms.set(false);
            const selectedPoints = await DataIngestionService.getIngestionPointsByIds(ingestionPointIds);
            this.selectedForms.set(selectedPoints);
        } else {
            this.searchAllForms.set(false);
            this.selectedForms.set([]);
        }
    }

    /**
     * Build common request fields shared by SearchRequest and ReportRequest
     */
    private buildCommonRequestFields(): {
        ingestionPointIds: number[];
        searchAllForms: boolean;
        predicate: PredicateNode | null;
        organizationIds?: number[];
        eventIds?: number[];
    } {
        return {
            ingestionPointIds: this.selectedForms().map((f) => f.ingestionPointId!),
            searchAllForms: this.searchAllForms(),
            predicate: this.predicate(),
            organizationIds: this.selectedOrganizationIds().length > 0 ? this.selectedOrganizationIds() : undefined,
            eventIds: this.selectedEventIds().length > 0 ? this.selectedEventIds() : undefined,
        };
    }

    /**
     * Destroy and recreate multiselect components to ensure they properly initialize with current selectedItems
     */
    recreateMultiselects() {
        this.showMultiselects.set(false);
        setTimeout(() => {
            this.showMultiselects.set(true);
        }, 0);
    }

    private onSearchCriteriaChanged() {
        this.resetPageKey();
        this.loadAvailableFields();
    }

    onFormsChanged(forms: IngestionPoint[]) {
        this.selectedForms.set(forms);
        this.onSearchCriteriaChanged();
    }

    onSearchAllChanged(searchAll: boolean) {
        this.searchAllForms.set(searchAll);
        this.onSearchCriteriaChanged();
    }

    selectedOrganizationsWithNames = signal<S25ItemI[]>([]);
    selectedEventsWithNames = signal<S25ItemI[]>([]);

    readonly selectedOrganizations = computed(() => this.selectedOrganizationsWithNames());
    readonly selectedEvents = computed(() => this.selectedEventsWithNames());

    getSelectedOrganizations(): S25ItemI[] {
        return this.selectedOrganizations();
    }

    getSelectedEvents(): S25ItemI[] {
        return this.selectedEvents();
    }

    private extractItemIds(items: S25ItemI[]): number[] {
        return items
            .map((item) => item.itemId!)
            .filter((id): id is number => id !== undefined && typeof id === "number");
    }

    onOrganizationsChanged(items: S25ItemI[]) {
        const ids = this.extractItemIds(items);
        this.selectedOrganizationIds.set(ids);
        this.selectedOrganizationsWithNames.set(items);
        this.onSearchCriteriaChanged();
    }

    onEventsChanged(items: S25ItemI[]) {
        const ids = this.extractItemIds(items);
        this.selectedEventIds.set(ids);
        this.selectedEventsWithNames.set(items);
        this.onSearchCriteriaChanged();
    }

    /**
     * Load names for organization and event IDs and update the signals
     */
    private async loadOrganizationAndEventNames() {
        const orgIds = this.selectedOrganizationIds();
        const eventIds = this.selectedEventIds();

        const itemsToLoad: S25ItemI.NumId[] = [
            ...orgIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Organization })),
            ...eventIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Event })),
        ];

        if (itemsToLoad.length === 0) {
            this.selectedOrganizationsWithNames.set([]);
            this.selectedEventsWithNames.set([]);
            return;
        }

        try {
            const namesMap = await NameService.getNames(itemsToLoad);

            // NameService.getNames returns a map with keys in format "itemId-itemTypeId"
            const keySep = "-";
            const orgs: S25ItemI[] = orgIds.map((id) => {
                const key = `${id}${keySep}${Item.Ids.Organization}`;
                return {
                    itemId: id,
                    itemTypeId: Item.Ids.Organization,
                    itemName: namesMap[key] || `Organization ${id}`,
                };
            });

            const events: S25ItemI[] = eventIds.map((id) => {
                const key = `${id}${keySep}${Item.Ids.Event}`;
                return {
                    itemId: id,
                    itemTypeId: Item.Ids.Event,
                    itemName: namesMap[key] || `Event ${id}`,
                };
            });

            this.selectedOrganizationsWithNames.set(orgs);
            this.selectedEventsWithNames.set(events);
        } catch (error) {
            S25Util.showError(error, "Error loading organization/event names");
            // Fallback to items without names
            this.selectedOrganizationsWithNames.set(
                orgIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Organization })),
            );
            this.selectedEventsWithNames.set(eventIds.map((id) => ({ itemId: id, itemTypeId: Item.Ids.Event })));
        }
    }

    onFieldSelectionChanged(fields: ReportingFieldInfo[]) {
        this.selectedFieldInfos.set(fields);
        this.resetPageKey();
    }

    getSelectedFieldsForSelector(): ReportingFieldInfo[] {
        return this.selectedFieldInfos();
    }

    onAggregationsChanged(aggregations: AggregationSpec[]) {
        this.selectedAggregations.set(aggregations);
    }

    onPredicateChanged(predicate: any) {
        this.predicate.set(predicate);
        this.resetPageKey();
    }

    resetPageKey() {
        this.currentPageKey.set(null);
    }

    async loadAvailableFields(includeDeleted = false) {
        if (this.selectedForms().length === 0 && !this.searchAllForms()) {
            this.availableFields.set([]);
            return;
        }

        this.loading.set(true);
        try {
            const formIds = this.selectedForms().map((f) => f.ingestionPointId!);
            const fields = await DataIngestionService.getDistinctReportingFields(
                formIds,
                this.searchAllForms(),
                includeDeleted,
            );
            this.availableFields.set(fields);
        } catch (error) {
            S25Util.showError(error, "Error loading available fields");
        } finally {
            this.loading.set(false);
        }
    }

    canExecute(): boolean {
        if (this.mode() === "search") {
            return this.selectedFieldInfos().length > 0;
        } else {
            // For reports, need at least one complete aggregation
            return (
                this.selectedFieldInfos().length > 0 &&
                this.selectedAggregations().length > 0 &&
                this.selectedAggregations().every((agg) => this.isAggregationComplete(agg))
            );
        }
    }

    isAggregationComplete(aggregation: AggregationSpec): boolean {
        switch (aggregation.type) {
            case "COUNT":
                return aggregation.field === "*";
            case "COUNT_DISTINCT":
                return (
                    aggregation.field === "cont_id" ||
                    this.availableFields().some((f) => f.reportingFieldCode === aggregation.field)
                );
            case "SUM":
            case "AVG":
                return this.availableFields().some(
                    (f) =>
                        f.reportingFieldCode === aggregation.field &&
                        (f.answerType === AnswerType.INTEGER || f.answerType === AnswerType.FLOAT),
                );
            default:
                return false;
        }
    }

    canSave(): boolean {
        return this.canExecute();
    }

    canSaveAs(): boolean {
        return this.canExecute() && this.currentSavedQuery() !== null;
    }

    async loadSavedQuery(event: Event) {
        const target = event.target as HTMLSelectElement;
        const queryId = parseInt(target.value);

        if (!queryId) {
            this.currentSavedQuery.set(null);
            this.saveName.set("");
            return;
        }

        try {
            const queryWithData = await DataIngestionService.getSavedQuery(queryId);
            if (queryWithData) {
                this.currentSavedQuery.set(queryWithData);
                await this.loadSavedQueryData(queryWithData);
                // Set query name after loadSavedQueryData completes (since setMode clears it)
                this.saveName.set(queryWithData.queryName);
            }
        } catch (error) {
            S25Util.showError(error, "Error loading saved query");
        }
    }

    async loadSavedQueryData(query: SavedQueryWithData) {
        try {
            if (query.queryType === "SEARCH" && query.searchRequest) {
                await this.loadSearchQueryData(query.searchRequest);
            } else if (query.queryType === "REPORT" && query.reportRequest) {
                await this.loadReportQueryData(query.reportRequest);
            }
        } catch (error) {
            S25Util.showError(error, "Error loading saved query data");
        }
    }

    private async loadSearchQueryData(req: SearchRequest) {
        // Reset state and set mode
        this.resetModeState();

        // Load forms
        await this.loadFormsFromIds(req.ingestionPointIds, req.searchAllForms);

        // Load organization and event filters
        this.selectedOrganizationIds.set(req.organizationIds || []);
        this.selectedEventIds.set(req.eventIds || []);

        // Load names for organizations and events
        await this.loadOrganizationAndEventNames();

        // Recreate multiselects with new selected items
        this.recreateMultiselects();

        // Load fields
        await this.loadSearchFields(req.projectionFields);

        // Load predicate (normalize to restore frontend-only properties lost during save/load)
        const predicate = req.predicate ? this.normalizePredicate(req.predicate) : null;
        this.predicate.set(predicate);
    }

    private async loadReportQueryData(req: ReportRequest) {
        // Reset state and set mode
        this.resetModeState();

        // Load forms
        await this.loadFormsFromIds(req.ingestionPointIds, req.searchAllForms);

        // Load organization and event filters
        this.selectedOrganizationIds.set(req.organizationIds || []);
        this.selectedEventIds.set(req.eventIds || []);

        // Load names for organizations and events
        await this.loadOrganizationAndEventNames();

        // Recreate multiselects with new selected items
        this.recreateMultiselects();

        // Load fields and aggregations
        await this.loadReportFields(req.groupByFields);
        this.selectedAggregations.set(req.aggregations || []);

        // Load predicate
        this.predicate.set(req.predicate || null);
    }

    private async loadSearchFields(projectionFields?: string[]) {
        if (projectionFields && projectionFields.length > 0) {
            await this.loadAvailableFields(true);
            const available = this.availableFields();
            const selected = available.filter((f) => projectionFields.includes(f.reportingFieldCode));
            this.selectedFieldInfos.set(selected);
        } else {
            this.selectedFieldInfos.set([]);
        }
    }

    private async loadReportFields(groupByFields?: ReportingFieldInfo[]) {
        if (groupByFields) {
            await this.loadAvailableFields(true);
            const available = this.availableFields();
            const selected = groupByFields.map((gf) => {
                const found = available.find((f) => f.reportingFieldCode === gf.reportingFieldCode);
                if (found) {
                    // Preserve dateModifier from saved field if it exists
                    return { ...found, dateModifier: gf.dateModifier };
                }
                return gf;
            });
            this.selectedFieldInfos.set(selected);
        } else {
            this.selectedFieldInfos.set([]);
        }
    }

    async deleteSavedQuery() {
        if (!this.currentSavedQuery()) return;

        const itemType = this.mode() === "search" ? "search" : "aggregation";
        const confirmed = await AlertService.confirm(`Are you sure you want to delete this saved ${itemType}?`);
        if (!confirmed) return;

        try {
            await DataIngestionService.deleteSavedQuery(this.currentSavedQuery()!.queryId);
            this.currentSavedQuery.set(null);
            this.saveName.set("");
            await this.loadSavedQueries(); // Refresh the list
        } catch (error) {
            S25Util.showError(error, `Error deleting saved ${itemType}`);
        }
    }

    async save() {
        if (!this.canSave() || !this.saveName()?.trim()) return;
        if (this.showPredicateValidationError()) return;

        const savedQuery = this.currentSavedQuery();
        if (savedQuery?.queryId) {
            await this.updateSavedQuery();
        } else {
            await this.createSavedQuery(this.saveName()!.trim());
        }
    }

    async saveAs() {
        if (!this.canSave() || !this.saveName()?.trim()) return;
        if (this.showPredicateValidationError()) return;

        this.currentSavedQuery.set(null);
        await this.createSavedQuery(this.saveName()!.trim());
    }

    private buildSearchRequest(): SearchRequest {
        return {
            ...this.buildCommonRequestFields(),
            projectionFields: this.selectedFieldInfos().map((f) => f.reportingFieldCode),
            pageNum: 1,
            pageSize: 50,
        };
    }

    private buildReportRequest(): ReportRequest {
        return {
            ...this.buildCommonRequestFields(),
            groupByFields: this.selectedFieldInfos(),
            aggregations: this.selectedAggregations(),
            includeForm: this.selectedFieldInfos().some((f) => f.reportingFieldCode === "form"),
        };
    }

    async createSavedQuery(name: string) {
        try {
            if (this.mode() === "search") {
                await DataIngestionService.saveSearch(name, this.buildSearchRequest());
            } else {
                await DataIngestionService.saveReport(name, this.buildReportRequest());
            }
            this.successMessage.set(
                this.mode() === "search" ? "Search saved successfully" : "Aggregation saved successfully",
            );
            await this.loadSavedQueries(); // Refresh the list
        } catch (error) {
            S25Util.showError(error, this.mode() === "search" ? "Error saving search" : "Error saving aggregation");
        }
    }

    async updateSavedQuery() {
        if (!this.currentSavedQuery()) return;

        try {
            const name = this.saveName()?.trim();
            if (!name) {
                S25Util.showError(
                    null,
                    this.mode() === "search" ? "Search name is required" : "Aggregation name is required",
                );
                return;
            }

            if (this.mode() === "search") {
                await DataIngestionService.updateSavedSearch(
                    this.currentSavedQuery()!.queryId,
                    name,
                    this.buildSearchRequest(),
                );
            } else {
                await DataIngestionService.updateSavedReport(
                    this.currentSavedQuery()!.queryId,
                    name,
                    this.buildReportRequest(),
                );
            }
            this.successMessage.set(
                this.mode() === "search" ? "Search updated successfully" : "Aggregation updated successfully",
            );
            await this.loadSavedQueries(); // Refresh the list
        } catch (error) {
            S25Util.showError(error, this.mode() === "search" ? "Error updating search" : "Error updating aggregation");
        }
    }

    private normalizePredicate(node: PredicateNode): PredicateNode {
        const boolOps = new Set(["And", "Or"]);
        if (!node.id) {
            node.id = "node_" + S25Util.randomUUID();
        }
        if (node.op && !boolOps.has(node.op)) {
            node.type = PredicateType.FIELD;
            if (!node.valueType && node.columnName) {
                const field = this.availableFields()?.find((f) => f.reportingFieldCode === node.columnName);
                if (field) node.valueType = field.answerType;
            }
        }
        if (node.children) {
            node.children.forEach((child) => this.normalizePredicate(child));
        }
        return node;
    }

    private getEmptyPredicateFields(node: PredicateNode | null): string[] {
        if (!node) return [];
        if (node.op === "And" || node.op === "Or") {
            return node.children?.flatMap((child) => this.getEmptyPredicateFields(child)) ?? [];
        }
        if (!node.columnName) {
            return ["Unknown field"];
        }
        if (!node.op) {
            return [node.columnName];
        }
        if (node.op === "In" || node.op === "NotIn") {
            if (!node.value || (Array.isArray(node.value) && node.value.length === 0)) {
                return [node.columnName];
            }
            return [];
        }
        if (node.value === null || node.value === undefined || node.value === "") {
            return [node.columnName];
        }
        return [];
    }

    private showPredicateValidationError(): boolean {
        const emptyFields = [...new Set(this.getEmptyPredicateFields(this.predicate()))];
        if (!emptyFields.length) return false;
        S25Util.showError(
            null,
            `Search criteria incomplete: the following field(s) have no value: ${emptyFields.join(", ")}. Please add values or remove the empty criteria.`,
        );
        return true;
    }

    private showExecuteValidationError(): boolean {
        const errors: string[] = [];
        if (this.selectedFieldInfos().length === 0) {
            errors.push(
                this.mode() === "search"
                    ? "Please select at least one field to display."
                    : "Please select at least one field to group by.",
            );
        }
        if (this.mode() === "aggregations") {
            if (this.selectedAggregations().length === 0) {
                errors.push("Please add at least one aggregation.");
            } else if (!this.selectedAggregations().every((agg) => this.isAggregationComplete(agg))) {
                errors.push("One or more aggregations are incomplete. Please select a type and field for each.");
            }
        }
        if (!errors.length) return false;
        S25Util.showError(null, errors.join(" "));
        return true;
    }

    async execute() {
        if (this.showPredicateValidationError()) return;
        if (this.showExecuteValidationError()) return;
        this.loading.set(true);
        try {
            // Reset results and clear table configs to force rebuild
            this.results.set(null);
            if (this.mode() === "search") {
                this.searchTableConfig.set(null);
            } else {
                this.reportTableConfig.set(null);
            }

            setTimeout(async () => {
                // Build table config - let the table's data source handle the actual search/report
                await this.buildTableConfig();
                this.resultsChanged.emit(this.results());
            }, 0);
        } catch (error) {
            S25Util.showError(error, "Error executing search or aggregation");
        } finally {
            this.loading.set(false);
        }
    }

    async executeSearch(pageNum: number = 1, pageSize: number = 50): Promise<SearchResponse> {
        const request: SearchRequest = {
            ...this.buildSearchRequest(),
            pageNum: pageNum,
            pageSize: pageSize,
            pageKey: pageNum === 1 ? undefined : this.currentPageKey() || undefined,
        };

        const response = await DataIngestionService.executeSearch(request);

        // Update the current page key
        if (response.pageCacheKey) {
            this.currentPageKey.set(response.pageCacheKey);
        }

        return response;
    }

    async executeReport(): Promise<ReportResponse> {
        const request = this.buildReportRequest();
        return await DataIngestionService.executeReport(request);
    }

    async buildTableConfig() {
        if (this.mode() === "search") {
            await this.buildSearchTableConfig();
        } else {
            await this.buildReportTableConfig();
        }
    }

    // Note: Tables are kept separate and shown/hidden based on mode to avoid re-running WS calls

    async buildSearchTableConfig() {
        const columns: Table.Column[] = [
            { header: "Submission ID", id: "submissionId" },
            { header: "Form", id: "form" },
            { header: "Contact Name", id: "contactName" },
            { header: "Submission Date", id: "submissionDate", type: "date" },
        ];

        // Add selected field columns
        const selectedFields = this.selectedFieldInfos();
        selectedFields.forEach((field) => {
            columns.push({
                header: field.reportingFieldCode,
                id: field.reportingFieldCode,
            });
        });

        this.searchTableConfig.set({
            type: "paginated",
            columns,
            dataSource: async (query: Table.PaginatedQuery) => {
                // Don't run WS if no fields are selected (e.g., when switching modes)
                if (this.selectedFieldInfos().length === 0) {
                    return { rows: [], totalRows: 0 };
                }

                // Use the pure executeSearch function
                const response = await this.executeSearch(query.page + 1, query.pageSize || 50);

                // Load contact names and special field names for this page
                const pageContactNames = await this.loadContactNames(response.data as IngestionSubmission[]);
                const pageSpecialFieldNames = await this.loadSpecialFieldNames(response.data as IngestionSubmission[]);

                // Build rows for this page using the selected fields
                const pageRows = await this.buildRowsForPage(
                    response.data as IngestionSubmission[],
                    pageContactNames,
                    pageSpecialFieldNames,
                    selectedFields.map((f) => f.reportingFieldCode),
                );

                return { rows: pageRows, totalRows: response.total };
            },
            startPage: 0,
            defaultPageSize: 50,
            pageSizes: [25, 50, 100],
        });
    }

    async buildRowsForPage(
        submissions: IngestionSubmission[],
        contactNames: Map<number, string>,
        specialFieldNames: Map<string, string>,
        fieldColumns: string[],
    ): Promise<Table.Row[]> {
        return submissions.map((submission) => {
            const cells: Record<string, Table.Cell> = {
                submissionId: { text: submission.submissionId },
                form: { text: submission.ingestionPoint?.ingestionPointName || "" },
                contactName: { text: contactNames.get(submission.contId) || `Contact ${submission.contId}` },
                submissionDate: { text: this.formatDate(submission.submissionDate) },
            };

            // Add field values
            fieldColumns.forEach((field) => {
                cells[field] = { text: this.getFieldValue(submission, field, specialFieldNames) };
            });

            return {
                id: submission.submissionId,
                name: submission.submissionId.toString(),
                cells,
            };
        });
    }

    async buildReportTableConfig() {
        // Build columns from groupBy fields and aggregations (no need to execute report first)
        const columns: Table.Column[] = [];

        // Add group by field columns
        const groupByFields = this.selectedFieldInfos();
        if (groupByFields) {
            groupByFields.forEach((fieldInfo) => {
                if (fieldInfo.reportingFieldCode === "form") {
                    columns.push({ header: "Form", id: "form_name", sortable: true });
                } else if (fieldInfo.reportingFieldCode === "cont_id") {
                    columns.push({ header: "Contact ID", id: "cont_id", sortable: true });
                } else {
                    // Regular field - use the field name as both header and id
                    columns.push({
                        header: fieldInfo.reportingFieldCode,
                        id: fieldInfo.reportingFieldCode,
                        sortable: true,
                    });
                }
            });
        }

        // Add aggregation columns
        const aggregations = this.selectedAggregations();
        if (aggregations) {
            aggregations.forEach((agg) => {
                let header: string;
                let id: string;

                switch (agg.type) {
                    case "COUNT":
                        header = "Count";
                        id = "count_all";
                        break;
                    case "COUNT_DISTINCT":
                        if (agg.field === "cont_id") {
                            header = "Distinct Contacts";
                            id = "distinct_cont_id";
                        } else {
                            header = `Distinct ${agg.field}`;
                            id = `distinct_${agg.field}`;
                        }
                        break;
                    case "SUM":
                        header = `Sum of ${agg.field}`;
                        id = `sum_${agg.field}`;
                        break;
                    case "AVG":
                        header = `Average ${agg.field}`;
                        id = `avg_${agg.field}`;
                        break;
                    default:
                        header = `${agg.type} ${agg.field}`;
                        id = `${(agg.type as string).toLowerCase()}_${agg.field}`;
                }

                columns.push({ header, id, sortable: true });
            });
        }

        // Set the table config with proper columns
        this.reportTableConfig.set({
            type: "unpaginated",
            columns,
            dataSource: async (query: Table.UnpaginatedQuery) => {
                // Don't run WS if no fields/aggregations are selected (e.g., when switching modes)
                if (
                    this.selectedFieldInfos().length === 0 ||
                    this.selectedAggregations().length === 0 ||
                    !this.selectedAggregations().every((agg) => this.isAggregationComplete(agg))
                ) {
                    return { rows: [] };
                }

                // Execute the report and get the data (always execute - no caching for reports)
                const response = await this.executeReport();
                const reportData = response.data || [];

                if (reportData.length === 0) {
                    return { rows: [] };
                }

                const specialFieldNames = await this.loadNamesForSpecialFieldIds(
                    this.accumulateSpecialFieldIdsFromReport(reportData, this.selectedFieldInfos()),
                );

                // Build rows from the data
                const rows: Table.Row[] = reportData.map((row, index) => {
                    const cells: Record<string, Table.Cell> = {};

                    // Add cells for each column
                    columns.forEach((column) => {
                        cells[column.id] = {
                            text: this.getReportFieldValue(
                                row,
                                String(column.id),
                                this.selectedFieldInfos(),
                                specialFieldNames,
                            ),
                        };
                    });

                    return {
                        id: index,
                        name: index.toString(),
                        cells,
                    };
                });

                return { rows };
            },
        });
    }

    async loadContactNames(submissions: IngestionSubmission[]): Promise<Map<number, string>> {
        const contactIds = [...new Set(submissions.map((s) => s.contId).filter((id) => id))];

        if (contactIds.length === 0) {
            return new Map();
        }

        try {
            const contactNames = await ContactService.getContactsNameAndFormal(contactIds);
            return new Map(contactNames.map((c) => [Number(c.itemId), c.itemName]));
        } catch (error) {
            S25Util.showError(error, "Error fetching contact names");
            return new Map(contactIds.map((id) => [id, `Contact ${id}`]));
        }
    }

    /**
     * Accumulate special field IDs (EVENT_TYPE, EVENT, ORGANIZATION, CONTACT, ROLE, ORG_TYPE, CATEGORIES) from submissions
     */
    accumulateSpecialFieldIds(submissions: IngestionSubmission[]): Map<NameResolvableFieldType, Set<number>> {
        const idsByType = new Map<NameResolvableFieldType, Set<number>>(
            (Object.keys(NAME_RESOLVABLE_FIELD_TYPES) as NameResolvableFieldType[]).map((ft) => [ft, new Set()]),
        );

        for (const submission of submissions) {
            if (!submission.answers) continue;
            for (const answer of submission.answers) {
                const fieldType = answer.fieldDefinition?.fieldType;
                const answerRaw = answer.answerRaw;
                if (isNameResolvableFieldType(fieldType) && answerRaw && !isNaN(Number(answerRaw))) {
                    idsByType.get(fieldType)!.add(Number(answerRaw));
                }
            }
        }

        return idsByType;
    }

    /**
     * Accumulate special field IDs from report data using selected field metadata
     */
    accumulateSpecialFieldIdsFromReport(
        reportData: { [key: string]: string | number }[],
        selectedFieldInfos: ReportingFieldInfo[],
    ): Map<NameResolvableFieldType, Set<number>> {
        const idsByType = new Map<NameResolvableFieldType, Set<number>>(
            (Object.keys(NAME_RESOLVABLE_FIELD_TYPES) as NameResolvableFieldType[]).map((ft) => [ft, new Set()]),
        );

        const fieldCodeToType = new Map<string, NameResolvableFieldType>();
        for (const field of selectedFieldInfos) {
            if (isNameResolvableFieldType(field.fieldType)) {
                fieldCodeToType.set(field.reportingFieldCode, field.fieldType);
            }
        }

        for (const row of reportData) {
            for (const fieldCode of Object.keys(row)) {
                const fieldType = fieldCodeToType.get(fieldCode);
                if (!fieldType) continue;
                const value = row[fieldCode];
                if (value !== null && value !== undefined && !isNaN(Number(value))) {
                    idsByType.get(fieldType)!.add(Number(value));
                }
            }
        }

        return idsByType;
    }

    async loadNamesForSpecialFieldIds(
        idsByType: Map<NameResolvableFieldType, Set<number>>,
    ): Promise<Map<string, string>> {
        const nameMap = new Map<string, string>();

        let hasAny = false;
        for (const ids of idsByType.values()) {
            if (ids.size > 0) {
                hasAny = true;
                break;
            }
        }
        if (!hasAny) return nameMap;

        try {
            for (const [fieldType, ids] of idsByType) {
                if (ids.size > 0) {
                    const names = await this.getObjectNames(fieldType, Array.from(ids));
                    for (const [key, name] of Object.entries(names)) {
                        nameMap.set(key, name);
                    }
                }
            }
        } catch (error) {
            S25Util.showError(error, "Error loading special field names");
        }

        return nameMap;
    }

    /**
     * Get names for event types, organizations, contacts, roles, and categories
     * This method handles the specific object types used in ingestion forms
     */
    async getObjectNames(objectType: NameResolvableFieldType, objectIds: number[]): Promise<Record<string, string>> {
        //     } else if (objectType === "ELECTION_POSITION") {
        //     const items = await SearchCriteriaService.getElectionPositions(objectIds);
        //     const nameMap: { [key: string]: string } = {};
        //     for (const row of items) {
        //     if (row.itemId != null && !Number.isNaN(Number(row.itemId))) {
        //     nameMap[`${row.itemId}-ELECTION_POSITION`] = row.itemName ?? "";
        // }
        // }
        // return nameMap;
        // }
        if (!objectIds.length) return {};
        try {
            const criteriaType = NAME_RESOLVABLE_FIELD_TYPES[objectType].searchCriteriaType;
            const store = this.itemService.criteria(criteriaType);
            const items = await store.fetchAll(objectIds, { fetch: { all: true } });
            const nameMap: Record<string, string> = {};
            for (const item of items) {
                if (item.itemName) nameMap[`${item.itemId}-${objectType}`] = item.itemName;
            }
            return nameMap;
        } catch (error) {
            console.error(`Error loading ${objectType} names:`, error);
            return {};
        }
    }

    async loadSpecialFieldNames(submissions: IngestionSubmission[]): Promise<Map<string, string>> {
        return await this.loadNamesForSpecialFieldIds(this.accumulateSpecialFieldIds(submissions));
    }

    getFieldColumns(submissions: IngestionSubmission[]): string[] {
        const fieldCodes = new Set<string>();

        for (const submission of submissions) {
            if (submission.answers) {
                for (const answer of submission.answers) {
                    const fieldCode = answer.fieldDefinition?.reportingFieldCode;
                    if (fieldCode) {
                        fieldCodes.add(fieldCode);
                    }
                }
            }
        }

        return Array.from(fieldCodes).sort();
    }

    /**
     * Get field value for search submissions with special field name resolution
     */
    getFieldValue(submission: IngestionSubmission, fieldCode: string, specialFieldNames?: Map<string, string>): string {
        if (!submission.answers) {
            return "";
        }

        const answer = submission.answers.find(
            (a: IngestionFieldAnswer) => a.fieldDefinition?.reportingFieldCode === fieldCode,
        );

        if (!answer) {
            return "";
        }

        const fieldType = answer.fieldDefinition?.fieldType;
        const answerRaw = answer.answerRaw;

        if (fieldType === "CONTACT_ROLE" && answer.answerJson) {
            return this.formatContactRoleJson(answer.answerJson);
        }

        if (fieldType === "DURATION") {
            return this.formatDurationValue(answerRaw);
        }

        // Resolve special field names if available
        const resolvedValue = this.resolveSpecialFieldValue(fieldType, answerRaw, specialFieldNames);
        if (resolvedValue !== null) {
            return resolvedValue;
        }

        // Check if this is a date/time field and format accordingly
        if (answer.fieldDefinition?.answerType === AnswerType.DATETIME) {
            return this.formatDateTime(answerRaw);
        }

        return this.formatValue(answerRaw);
    }

    /**
     * Get field value for report rows with special field name resolution (DRY)
     */
    getReportFieldValue(
        row: { [key: string]: string | number },
        columnId: string,
        selectedFieldInfos: ReportingFieldInfo[],
        specialFieldNames: Map<string, string>,
    ): string {
        const value = row[columnId];
        if (value === null || value === undefined) {
            return "";
        }

        // Find the field info for this column to determine its type
        const fieldInfo = selectedFieldInfos.find((f) => {
            // Handle special column IDs that map to field codes
            if (columnId === "form_name") return f.reportingFieldCode === "form";
            if (columnId === "cont_id") return f.reportingFieldCode === "cont_id";
            return f.reportingFieldCode === columnId;
        });

        if (fieldInfo?.fieldType === "CONTACT_ROLE") {
            return this.formatContactRoleJson(value);
        }

        if (fieldInfo?.fieldType === "DURATION") {
            return this.formatDurationValue(value);
        }

        if (isNameResolvableFieldType(fieldInfo?.fieldType)) {
            const resolvedValue = this.resolveSpecialFieldValue(fieldInfo.fieldType, value, specialFieldNames);
            if (resolvedValue !== null) {
                return resolvedValue;
            }
        }

        // Handle date/time fields if this is a datetime field
        if (fieldInfo?.answerType === AnswerType.DATETIME) {
            return this.formatDateTime(value);
        }

        return this.formatValue(value);
    }

    /**
     * Resolve special field value (EVENT_TYPE, ORGANIZATION) to name if available (DRY helper)
     */
    resolveSpecialFieldValue(
        fieldType: string | undefined,
        value: string | number | null | undefined,
        specialFieldNames: Map<string, string> | undefined,
    ): string | null {
        if (!fieldType || !value || !specialFieldNames) {
            return null;
        }

        const numValue = Number(value);
        if (isNaN(numValue)) {
            return null;
        }

        // getObjectNames already appends the type to the key
        const key = `${numValue}-${fieldType}`;
        return specialFieldNames.get(key) || null;
    }

    formatContactRoleJson(value: string | number | null | undefined): string {
        if (!value) return "";
        try {
            const data = typeof value === "string" ? JSON.parse(value) : value;
            const items = data?.items || (Array.isArray(data) ? data : []);
            return items
                .map((item: any) => {
                    const role = item.roleName || `Role ${item.roleId}`;
                    const contact = item.contactName || `Contact ${item.contactId}`;
                    return `${role}: ${contact}`;
                })
                .join("; ");
        } catch {
            return String(value);
        }
    }

    formatDurationValue(value: any): string {
        if (!value) return "";
        try {
            const str = String(value);
            const timePart = str.includes("T") ? str.split("T")[1] : str;
            const [h, m] = timePart.split(":").map(Number);
            if (isNaN(h) && isNaN(m)) return str;
            if (h === 0 && m === 0) return "None";
            const parts = [];
            if (h) parts.push(h + " Hrs");
            if (m) parts.push(m + " Mins");
            return parts.length ? parts.join(", ") : "None";
        } catch {
            return String(value);
        }
    }

    formatDateTime(value: any): string {
        if (!value) {
            return "";
        }

        try {
            const format = this.dateTimeFormat();
            if (format) {
                return S25Datefilter.transform(value, format);
            }
            // Fallback to basic date formatting if no format loaded yet
            return this.formatDate(value);
        } catch (error) {
            console.error("Error formatting datetime:", error);
            // Fallback to basic date formatting
            return this.formatDate(value);
        }
    }

    formatValue(value: any): string {
        if (value === null || value === undefined) {
            return "";
        }

        if (typeof value === "object") {
            return JSON.stringify(value);
        }

        return String(value);
    }

    formatDate(date: any): string {
        if (!date) {
            return "";
        }

        try {
            const d = new Date(date);
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } catch (error) {
            return String(date);
        }
    }
}
